// src/components/Notes.js
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { auth, db, functions } from '../firebase';
import { useLocation, useParams } from 'react-router-dom';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  limit,
  startAfter,
  getDocs,
  serverTimestamp,
  or,
  and
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import NoteCard from './NoteCard';
import { toast } from 'react-toastify';
import { groupByDate } from '../utils/groupByDate';
import { useTranslation } from 'react-i18next';
import NoteDetail from './NoteDetail';
import { PAGE_SIZE } from '../constants';
import { trackNotesLoaded, trackNoteOpened, trackNoteDeleted, trackNotePinned, trackNoteUnpinned, trackNoteShare, trackNotePrint, trackNoteResend } from '../analytics';
import { userService } from '../services/UserService';
import {
  InboxIcon,
  DocumentTextIcon,
  ArchiveBoxIcon,
  FolderIcon,
  CheckIcon,
  LinkIcon
} from '@heroicons/react/24/outline';
import SearchBar from './common/SearchBar';
import FilterBar from './common/FilterBar';
import useBreakpoint from '../hooks/useBreakpoint';
import NoteContextMenu from './NoteContextMenu';
import FolderSelectionModal from './FolderSelectionModal';
import NoteShareModal from './NoteShareModal';

function Notes() {
  const { t } = useTranslation();
  const [notes, setNotes] = useState([]);
  const [pinnedNotes, setPinnedNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // 'all', 'tasks', 'notes', 'links'
  const [selectedNoteId, setSelectedNoteId] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [folders, setFolders] = useState([]);
  const filterAnchorRef = useRef(null);
  
  // Context menu state
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    note: null
  });
  
  // Folder selection modal
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [noteToMove, setNoteToMove] = useState(null);
  
  // Share modal
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [noteToShare, setNoteToShare] = useState(null);
  
  // Undo delete state
  const [pendingDeleteNotes, setPendingDeleteNotes] = useState([]);
  const [showUndoToast, setShowUndoToast] = useState(false);
  const deleteTimerRef = useRef(null);
  
  // User data for secondary emails
  const [userData, setUserData] = useState(null);
  
  // Resizable panel state
  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = localStorage.getItem('noteDetailPanelWidth');
    return saved ? parseInt(saved, 10) : 480;
  });
  const [isResizing, setIsResizing] = useState(false);
  const MIN_PANEL_WIDTH = 420;

  const location = useLocation();
  const { folderId } = useParams();
  const { isDesktop } = useBreakpoint();

  // Panel resize handlers
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isResizing) return;
    
    const newWidth = window.innerWidth - e.clientX;
    if (newWidth >= MIN_PANEL_WIDTH && newWidth <= window.innerWidth * 0.8) {
      setPanelWidth(newWidth);
    }
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    if (isResizing) {
      setIsResizing(false);
      localStorage.setItem('noteDetailPanelWidth', panelWidth.toString());
    }
  }, [isResizing, panelWidth]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const filterOptions = [
    { id: 'all', label: t('notes.filters.all') },
    { id: 'tasks', label: t('notes.filters.tasks'), icon: CheckIcon },
    { id: 'notes', label: t('notes.filters.notes'), icon: DocumentTextIcon },
    { id: 'links', label: t('notes.filters.links'), icon: LinkIcon },
  ];

  const notesRef = useMemo(() => collection(db, 'notes'), []);
  const unsubscribeRef = useRef([]);
  const isFetchingRef = useRef(false);
  
  // Subscribe to userService for folders and user data
  useEffect(() => {
    const updateUserData = (user) => {
      if (user) {
        setUserData(user);
        if (user.folders) {
          setFolders(user.folders);
        } else {
          setFolders([]);
        }
      } else {
        setUserData(null);
        setFolders([]);
      }
    };

    // Initial load
    updateUserData(userService.currentUser);

    // Subscribe to changes
    const unsubscribe = userService.subscribe(updateUserData);

    return unsubscribe;
  }, []);
  
  const getFolderName = useCallback((folderId) => {
    if (!folderId || folderId === 'inbox') return t('notes.inbox');
    if (folderId === 'archived') return t('notes.archive');
    
    const folder = folders.find(f => f.id === folderId);
    return folder ? folder.name : t('common.unknown');
  }, [folders, t]);

  // Get header title based on current location/folder
  const getHeaderInfo = () => {
    if (location.pathname === '/inbox' || location.pathname === '/' || location.pathname === '/notes') {
      return { title: t('notes.inbox'), icon: <InboxIcon className="h-8 w-8 text-gray-900" /> };
    } else if (location.pathname === '/all') {
      return { title: t('notes.allNotes'), icon: <DocumentTextIcon className="h-8 w-8 text-gray-900" /> };
    } else if (location.pathname === '/archive') {
      return { title: t('notes.archive'), icon: <ArchiveBoxIcon className="h-8 w-8 text-gray-900" /> };
    } else if (folderId) {
      const name = getFolderName(folderId);
      return { title: name, icon: <FolderIcon className="h-8 w-8 text-gray-900" /> };
    }
    return { title: t('notes.allNotes'), icon: null };
  };


  // Dual listeners effect - one for pinned notes, one for regular notes
  useEffect(() => {
    console.log('useEffect triggered', location.pathname, folderId);

    if (!auth.currentUser) {
      setNotes([]);
      setPinnedNotes([]);
      setLoading(false);
      setHasMore(false);
      return;
    }

    setLoading(true);
    setNotes([]);
    setPinnedNotes([]);

    // Build base filters for both queries
    const baseFilters = [
      where('uid', '==', auth.currentUser.uid),
      where('isPendingtoDelete', '==', false) // CRITICAL: Always filter soft-deleted notes
    ];

    // Apply folder filters based on route
    if (location.pathname === '/inbox' || location.pathname === '/' || location.pathname === '/notes') {
      baseFilters.push(where('folderId', '==', 'inbox'));
    } else if (location.pathname === '/archive') {
      baseFilters.push(where('folderId', '==', 'archived'));
    } else if (folderId) {
      baseFilters.push(where('folderId', '==', folderId));
    }

    // Apply Type Filter for regular notes
    const regularFilters = [...baseFilters, where('isPinned', '==', false)];
    
    if (filterType === 'tasks') {
      regularFilters.push(where('noteType', '==', 0));
    } else if (filterType === 'notes') {
      regularFilters.push(where('noteType', 'in', [1, 2, 3, 4]));
    } else if (filterType === 'links') {
      regularFilters.push(where('hasLinks', '==', true));
    } else {
      if ((location.pathname === '/inbox' || location.pathname === '/' || location.pathname === '/notes') && filterType === 'all') {
        regularFilters.push(or(
          where('noteType', 'in', [1, 2, 3, 4]),
          where('isTodoCompleted', '==', false)
        ));
      }
    }

    // Apply same type filters for pinned notes
    const pinnedFilters = [...baseFilters, where('isPinned', '==', true)];
    
    if (filterType === 'tasks') {
      pinnedFilters.push(where('noteType', '==', 0));
    } else if (filterType === 'notes') {
      pinnedFilters.push(where('noteType', 'in', [1, 2, 3, 4]));
    } else if (filterType === 'links') {
      pinnedFilters.push(where('hasLinks', '==', true));
    } else {
      if ((location.pathname === '/inbox' || location.pathname === '/' || location.pathname === '/notes') && filterType === 'all') {
        pinnedFilters.push(or(
          where('noteType', 'in', [1, 2, 3, 4]),
          where('isTodoCompleted', '==', false)
        ));
      }
    }

    // LISTENER 1: Pinned Notes
    const qPinned = query(
      notesRef,
      and(...pinnedFilters),
      orderBy('creationDate', 'desc'),
      limit(20) // Reasonable limit for pinned notes
    );

    const unsubscribePinned = onSnapshot(qPinned, (snapshot) => {
      console.log("Pinned notes snapshot received", snapshot.docs.length, "documents");
      console.log("Pinned notes filters:", pinnedFilters);
      console.log("Current folderId:", folderId);
      console.log("Current filterType:", filterType);
      
      const pinnedNotesArray = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        folderName: getFolderName(doc.data().folderId)
      }));
      
      console.log("Pinned notes array:", pinnedNotesArray.map(n => ({ id: n.id, folderId: n.folderId, isPinned: n.isPinned })));
      
      setPinnedNotes(pinnedNotesArray);
    });

    // LISTENER 2: Regular Notes (excluding pinned)
    const qRegular = query(
      notesRef,
      and(...regularFilters),
      orderBy('creationDate', 'desc'),
      limit(PAGE_SIZE)
    );

    trackNotesLoaded(0);

    const unsubscribeRegular = onSnapshot(qRegular, (snapshot) => {
      console.log("Regular notes snapshot received", snapshot.docs.length, "documents");
      
      const notesArray = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        folderName: getFolderName(doc.data().folderId)
      }));
      
      setNotes(notesArray);

      if (snapshot.docs.length > 0) {
        setLastDoc(snapshot.docs[snapshot.docs.length - 1]);
      } else {
        setLastDoc(null);
      }

      setHasMore(snapshot.docs.length === PAGE_SIZE);
      setLoading(false);
    });

    unsubscribeRef.current.push(unsubscribePinned, unsubscribeRegular);

    return () => {
      console.log('Cleaning up listeners');
      unsubscribeRef.current.forEach((unsub) => unsub());
      unsubscribeRef.current = [];
    };
  }, [notesRef, location.pathname, folderId, getFolderName, filterType]);

  const fetchMoreNotes = useCallback(async () => {
    if (!auth.currentUser) {
      setHasMore(false);
      return;
    }

    if (!lastDoc || loadingMore || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setLoadingMore(true);

    // Build filters
    const filters = [
      where('uid', '==', auth.currentUser.uid),
      where('isPendingtoDelete', '==', false)
    ];

    if (location.pathname === '/inbox' || location.pathname === '/' || location.pathname === '/notes') {
      filters.push(where('folderId', '==', 'inbox'));
    } else if (location.pathname === '/archive') {
      filters.push(where('folderId', '==', 'archived'));
    } else if (folderId) {
      filters.push(where('folderId', '==', folderId));
    }

    // Apply Type Filter
    if (filterType === 'tasks') {
      filters.push(where('noteType', '==', 0));
    } else if (filterType === 'notes') {
      filters.push(where('noteType', 'in', [1, 2, 3, 4]));
    } else if (filterType === 'links') {
      filters.push(where('hasLinks', '==', true));
    } else {
      // Default 'all' behavior
      if ((location.pathname === '/inbox' || location.pathname === '/' || location.pathname === '/notes') && filterType === 'all') {
         filters.push(or(
          where('noteType', 'in', [1, 2, 3, 4]),
          where('isTodoCompleted', '==', false)
        ));
      }
    }

    // Construct query with and() for filters, then orderBy, startAfter, limit
    const qMore = query(
      notesRef,
      and(...filters),
      orderBy('creationDate', 'desc'),
      startAfter(lastDoc),
      limit(PAGE_SIZE)
    );

    try {
      console.log('Fetching more notes');
      const querySnapshot = await getDocs(qMore);
      let newNotes = querySnapshot.docs.map((doc) => {
        const noteData = doc.data();
        return {
          id: doc.id,
          ...noteData,
          folderName: getFolderName(noteData.folderId)
        };
      });

      // For inbox, filter out completed todos
      // Client-side filtering removed as it's now in the query
      /*
      if (location.pathname === '/inbox' || location.pathname === '/' || location.pathname === '/notes') {
        newNotes = newNotes.filter(note => {
          return note.noteType !== 0 || !note.isTodoCompleted;
        });
      }
      */

      setNotes((prevNotes) => {
        const notesMap = new Map();
        prevNotes.forEach(note => notesMap.set(note.id, note));
        newNotes.forEach(note => notesMap.set(note.id, note));
        return Array.from(notesMap.values()).sort(
          (a, b) => b.creationDate.toDate() - a.creationDate.toDate()
        );
      });

      const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
      setLastDoc(lastVisible || null);
      setHasMore(querySnapshot.size === PAGE_SIZE);
    } catch (error) {
      console.error('Error fetching more notes:', error);
      toast.error(t('notes.actions.failedToLoadMore'));
    } finally {
      setLoadingMore(false);
      isFetchingRef.current = false;
    }
  }, [lastDoc, notesRef, loadingMore, location.pathname, folderId, getFolderName, filterType, t]);

  const toggleTodoCompletion = async (noteId, currentStatus) => {
    const isInbox = location.pathname === '/inbox' || location.pathname === '/' || location.pathname === '/notes';
    const newStatus = !currentStatus;
    
    // If in inbox and marking as completed, remove from list immediately
    if (isInbox && newStatus) {
      setNotes((prevNotes) => prevNotes.filter(note => note.id !== noteId));
    } else {
      // Otherwise, just update the status
      setNotes((prevNotes) =>
        prevNotes.map((note) =>
          note.id === noteId
            ? { ...note, isTodoCompleted: newStatus }
            : note
        )
      );
    }

    try {
      const noteRef = doc(db, 'notes', noteId);
      await updateDoc(noteRef, {
        isTodoCompleted: newStatus,
        ...(newStatus ? { todoCompletionDate: serverTimestamp() } : { todoCompletionDate: null })
      });
      toast.success(
        newStatus ? t('notes.actions.taskMarkedCompleted') : t('notes.actions.taskMarkedIncomplete')
      );
    } catch (error) {
      console.error('Error updating todo status:', error);
      // Revert the change on error
      setNotes((prevNotes) =>
        prevNotes.map((note) =>
          note.id === noteId
            ? { ...note, isTodoCompleted: currentStatus }
            : note
        )
      );
      toast.error(t('notes.actions.failedToUpdateNote'));
    }
  };

  // Context Menu Handlers
  const handleContextMenu = (e, note) => {
    e.preventDefault();
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      note
    });
  };

  const closeContextMenu = () => {
    setContextMenu({ visible: false, x: 0, y: 0, note: null });
  };

  // Pin/Unpin Handler
  const togglePinStatus = async (note) => {
    const newStatus = !note.isPinned;
    
    // Optimistic update: move between lists immediately
    if (newStatus) {
      // Moving to pinned
      setNotes((prev) => prev.filter(n => n.id !== note.id));
      setPinnedNotes((prev) => [{ ...note, isPinned: true }, ...prev]);
    } else {
      // Moving to regular
      setPinnedNotes((prev) => prev.filter(n => n.id !== note.id));
      setNotes((prev) => [{ ...note, isPinned: false }, ...prev]);
    }

    try {
      const noteRef = doc(db, 'notes', note.id);
      await updateDoc(noteRef, {
        isPinned: newStatus
      });
      toast.success(newStatus ? t('notes.actions.notePinned') : t('notes.actions.noteUnpinned'));
      
      // Track analytics
      if (newStatus) {
        trackNotePinned(note.id);
      } else {
        trackNoteUnpinned(note.id);
      }
    } catch (error) {
      console.error('Pin update failed:', error);
      toast.error(t('notes.actions.failedToUpdateNote'));
      
      // Revert optimistic update
      if (newStatus) {
        setPinnedNotes((prev) => prev.filter(n => n.id !== note.id));
        setNotes((prev) => [note, ...prev]);
      } else {
        setNotes((prev) => prev.filter(n => n.id !== note.id));
        setPinnedNotes((prev) => [note, ...prev]);
      }
    }
  };

  // Move to Folder Handler
  const handleMoveNote = (note) => {
    setNoteToMove(note);
    setIsFolderModalOpen(true);
  };

  const handleChangeNoteType = async (note, newType) => {
    // Optimistic update
    const updateNote = (n) => n.id === note.id ? { ...n, noteType: newType } : n;
    setNotes((prev) => prev.map(updateNote));
    setPinnedNotes((prev) => prev.map(updateNote));

    try {
      const noteRef = doc(db, 'notes', note.id);
      await updateDoc(noteRef, {
        noteType: newType,
        lastModified: serverTimestamp()
      });
      toast.success(t('notes.actions.noteTypeUpdated'));
    } catch (error) {
      console.error('Error updating note type:', error);
      toast.error(t('notes.actions.failedToUpdateNoteType'));
      
      // Revert optimistic update
      const revertNote = (n) => n.id === note.id ? { ...n, noteType: note.noteType } : n;
      setNotes((prev) => prev.map(revertNote));
      setPinnedNotes((prev) => prev.map(revertNote));
    }
  };

  const handleFolderSelect = async (folderId) => {
    if (!noteToMove) return;

    const oldFolderId = noteToMove.folderId;
    
    // Optimistic update
    const updateNote = (n) => n.id === noteToMove.id ? { ...n, folderId, folderName: getFolderName(folderId) } : n;
    setNotes((prev) => prev.map(updateNote));
    setPinnedNotes((prev) => prev.map(updateNote));

    try {
      const noteRef = doc(db, 'notes', noteToMove.id);
      await updateDoc(noteRef, {
        folderId,
        lastModified: serverTimestamp()
      });
      toast.success(t('notes.actions.movedTo', { folder: getFolderName(folderId) }));
    } catch (error) {
      console.error('Error moving note:', error);
      toast.error(t('notes.detail.failedToMoveNote'));
      
      // Revert on error
      const revertNote = (n) => n.id === noteToMove.id ? { ...n, folderId: oldFolderId, folderName: getFolderName(oldFolderId) } : n;
      setNotes((prev) => prev.map(revertNote));
      setPinnedNotes((prev) => prev.map(revertNote));
    } finally {
      setIsFolderModalOpen(false);
      setNoteToMove(null);
    }
  };

  // Share Handler
  const handleShareNote = async (note) => {
    setNoteToShare(note);
    setIsShareModalOpen(true);
    trackNoteShare(note.id);
  };

  // Print Handler
  const handlePrintNote = async (note) => {
    try {
      const getNoteHtml = httpsCallable(functions, 'getNoteHtml');
      const result = await getNoteHtml({ docId: note.id });
      
      const { html } = result.data;
      
      // Open print window with generated HTML
      const printWindow = window.open('', '_blank');
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
      
      // Track analytics
      trackNotePrint(note.id);
    } catch (error) {
      console.error('Error printing note:', error);
      toast.error(t('notes.actions.errorPreparingPrint'));
    }
  };

  // Resend Email Handler
  const handleResendEmail = async (note, emailAddress = null) => {
    const targetEmail = emailAddress || userData?.sendEmailAddress || userData?.email || 'primary email';
    const confirmed = window.confirm(t('notes.actions.resendNoteTo', { email: emailAddress || targetEmail }));
    
    if (!confirmed) return;
    
    try {
      const processNote = httpsCallable(functions, 'processNote');
      await processNote({
        docId: note.id,
        processTranscript: false,
        processSummary: false,
        processMetadata: false,
        processSendEmail: true,
        secondarySendEmail: emailAddress || undefined
      });
      
      toast.success(t('notes.actions.noteResentSuccessfully'));
      
      // Track analytics
      trackNoteResend(note.id);
    } catch (error) {
      console.error('Resend failed:', error);
      toast.error(t('notes.actions.failedToResend'));
    }
  };

  // Delete Handler (Soft Delete with Undo)
  const handleDeleteNote = async (note) => {
    // Add to pending queue
    setPendingDeleteNotes((prev) => [...prev, note]);
    
    // Optimistic UI removal
    setNotes((prev) => prev.filter(n => n.id !== note.id));
    setPinnedNotes((prev) => prev.filter(n => n.id !== note.id));
    
    // Firestore soft delete
    try {
      const noteRef = doc(db, 'notes', note.id);
      await updateDoc(noteRef, {
        isPendingtoDelete: true
      });
      
      // Show undo toast
      setShowUndoToast(true);
      
      // Clear any existing timer
      if (deleteTimerRef.current) {
        clearTimeout(deleteTimerRef.current);
      }
      
      // Start 3-second timer
      deleteTimerRef.current = setTimeout(() => {
        confirmDelete();
      }, 3000);
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error(t('notes.actions.failedToDelete'));
      
      // Revert on error
      setPendingDeleteNotes((prev) => prev.filter(n => n.id !== note.id));
      if (note.isPinned) {
        setPinnedNotes((prev) => [...prev, note]);
      } else {
        setNotes((prev) => [...prev, note]);
      }
    }
  };

  const confirmDelete = () => {
    // Clear pending queue
    setPendingDeleteNotes([]);
    setShowUndoToast(false);
    deleteTimerRef.current = null;
  };

  const undoDelete = async () => {
    // Clear timer
    if (deleteTimerRef.current) {
      clearTimeout(deleteTimerRef.current);
      deleteTimerRef.current = null;
    }
    
    // Restore all pending notes in Firestore
    // The listeners will automatically add them back to the UI
    for (const note of pendingDeleteNotes) {
      try {
        const noteRef = doc(db, 'notes', note.id);
        await updateDoc(noteRef, {
          isPendingtoDelete: false
        });
      } catch (error) {
        console.error('Error restoring note:', error);
        toast.error('Failed to restore note');
      }
    }
    
    setPendingDeleteNotes([]);
    setShowUndoToast(false);
    toast.success(t('notes.actions.noteRestored'));
  };

  const groupNotes = (notes) => {
    if (filterType === 'tasks') {
      const pending = [];
      const completed = [];

      notes.forEach((note) => {
        if (note.isTodoCompleted) {
          completed.push(note);
        } else {
          pending.push(note);
        }
      });

      // Sort pending by creationDate desc
      pending.sort((a, b) => {
        const dateA = a.creationDate?.toDate?.() || new Date(0);
        const dateB = b.creationDate?.toDate?.() || new Date(0);
        return dateB - dateA;
      });

      // Sort completed by todoCompletionDate desc
      completed.sort((a, b) => {
        const dateA = a.todoCompletionDate?.toDate?.() || new Date(0);
        const dateB = b.todoCompletionDate?.toDate?.() || new Date(0);
        return dateB - dateA;
      });

      return [
        [t('notes.groups.pending'), pending],
        [t('notes.groups.completed'), completed],
      ];
    }

    return groupByDate(notes, t);
  };

  const openNoteDetail = (noteId) => {
    setSelectedNoteId(noteId);
    trackNoteOpened(noteId);
  };

  const openNoteInNewWindow = (noteId) => {
    trackNoteOpened(noteId);
    const url = `${window.location.origin}/note/${noteId}`;
    const w = 820;
    const h = 700;
    const left = Math.max(0, Math.floor((window.screen.width - w) / 2));
    const top = Math.max(0, Math.floor((window.screen.height - h) / 2));
    const features = `width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`;
    window.open(url, '_blank', features);
  };

  const closeNoteDetail = () => {
    setSelectedNoteId(null);
  };

  const handleNoteDelete = (deletedNoteId) => {
    setNotes((prevNotes) => prevNotes.filter((note) => note.id !== deletedNoteId));
    trackNoteDeleted(deletedNoteId);
  };

  const filteredNotes = useMemo(() => {
    if (!searchTerm) return notes;
    const term = searchTerm.toLowerCase();
    return notes.filter(note => {
      return (
        note.title?.toLowerCase().includes(term) ||
        note.summary?.toLowerCase().includes(term) ||
        note.summary_short?.toLowerCase().includes(term) ||
        note.transcript?.toLowerCase().includes(term)
      );
    });
  }, [notes, searchTerm]);

  const filteredPinnedNotes = useMemo(() => {
    if (!searchTerm) return pinnedNotes;
    const term = searchTerm.toLowerCase();
    return pinnedNotes.filter(note => {
      return (
        note.title?.toLowerCase().includes(term) ||
        note.summary?.toLowerCase().includes(term) ||
        note.summary_short?.toLowerCase().includes(term) ||
        note.transcript?.toLowerCase().includes(term)
      );
    });
  }, [pinnedNotes, searchTerm]);

  // Handle drag start
  const handleDragStart = (e, note) => {
    e.dataTransfer.setData('text/plain', note.id);
    e.dataTransfer.setData('noteId', note.id);
    e.dataTransfer.effectAllowed = 'move';

    // Create custom drag image
    const ghost = document.createElement('div');
    ghost.style.width = '200px';
    ghost.style.backgroundColor = 'white';
    ghost.style.border = '1px solid #e5e7eb';
    ghost.style.borderRadius = '0.375rem';
    ghost.style.padding = '0.5rem';
    ghost.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
    ghost.style.position = 'absolute';
    ghost.style.top = '-1000px';
    ghost.style.left = '-1000px';
    ghost.style.zIndex = '-1000';
    
    // Content
    ghost.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px;">
        <span style="font-size: 20px;">${note.emoji || '📄'}</span>
        <span style="font-size: 14px; font-weight: 500; color: #374151; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
          ${note.title || t('common.note')}
        </span>
      </div>
    `;

    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 10, 25);

    // Clean up after a short delay
    setTimeout(() => {
      document.body.removeChild(ghost);
    }, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    );
  }

  if (notes.length === 0 && pinnedNotes.length === 0 && !loading) {
    let emptyMessage = t('notes.empty.noNotes');
    if (location.pathname === '/inbox') emptyMessage = t('notes.empty.inboxEmpty');
    else if (location.pathname === '/archive') emptyMessage = t('notes.empty.archiveEmpty');
    else if (folderId) emptyMessage = t('notes.empty.folderEmpty');
    
    // If filter is applied but no notes found
    if (filterType !== 'all') {
        if (filterType === 'tasks') emptyMessage = t('notes.empty.noTasks');
        else if (filterType === 'notes') emptyMessage = t('notes.empty.noNotesFound');
        else if (filterType === 'links') emptyMessage = t('notes.empty.noLinks');
    }
    
    const headerInfo = getHeaderInfo();

    return (
    <div className="md:pb-4 md:pt-0 h-full flex flex-col">
        {/* Responsive Header */}
      <div className="flex items-center justify-between mb-4 pt-4 md:pt-0 px-0">
            <div className="hidden md:flex items-center gap-x-3">
                {headerInfo.icon}
                <h1 className="text-3xl font-bold text-gray-900">{headerInfo.title}</h1>
            </div>
            
            {/* Search Bar */}
            <div className="w-full md:w-auto md:ml-auto flex items-center gap-2 md:gap-6">
                <div className="relative flex-1 md:flex-none md:w-48">
                    <SearchBar 
                        value={searchTerm}
                        onChange={setSearchTerm}
                        placeholder={t('notes.searchPlaceholder')}
                    />
                </div>
            </div>
        </div>

        {/* Filter Bar */}
        <FilterBar 
            options={filterOptions}
            selectedFilter={filterType}
            onFilterChange={setFilterType}
            className="mt-4 px-0"
        />

        <div className="flex items-center justify-center flex-grow h-64">
          <div className="text-gray-500">{emptyMessage}</div>
        </div>
      </div>
    );
  }

  // Combine pinned and regular notes with grouping
  const regularGroupedNotes = groupNotes(filteredNotes);
  
  // Debug: Log pinned notes state
  console.log("Render - pinnedNotes state:", pinnedNotes.length);
  console.log("Render - filteredPinnedNotes:", filteredPinnedNotes.length);
  console.log("Render - folderId:", folderId);
  console.log("Render - filterType:", filterType);
  
  const groupedNotes = [
    ...(filteredPinnedNotes.length > 0 ? [['PINNED', filteredPinnedNotes]] : []),
    ...regularGroupedNotes
  ];
  
  const selectedNote = selectedNoteId ? [...notes, ...pinnedNotes].find(note => note.id === selectedNoteId) : null;
  const headerInfo = getHeaderInfo();

  return (
    <div className="md:pb-4 md:pt-0 flex flex-col h-full px-2">
      {/* Responsive Header */}
        <div className="flex items-center justify-between mb-4 pt-4 md:pt-0 px-0">
            <div className="hidden md:flex items-center gap-x-3">
                {headerInfo.icon}
                <h1 className="text-3xl font-bold text-gray-900">{headerInfo.title}</h1>
            </div>
            
            {/* Search Bar */}
            <div className="w-full md:w-auto md:ml-auto flex items-center gap-2 md:gap-6">
                <div className="relative flex-1 md:flex-none md:w-48">
                    <SearchBar 
                        value={searchTerm}
                        onChange={setSearchTerm}
                        placeholder={t('notes.searchPlaceholder')}
                    />
                </div>
            </div>
        </div>

      <div className="flex-1 flex flex-col relative">
        <div ref={filterAnchorRef}>
          <FilterBar 
              options={filterOptions}
              selectedFilter={filterType}
              onFilterChange={setFilterType}
              className="mt-4 mb-8 px-0"
          />
        </div>

        <div className="flex-1 overflow-y-auto px-0">
          {groupedNotes.map(([groupName, groupNotes]) => (
            groupNotes.length > 0 && (
              <div key={groupName} className="mb-4 md:mb-8">
                <h2 className="text-xl md:text-lg font-bold mb-2 md:mb-4 text-gray-800">
                  {groupName}
                </h2>
                <div className="flex flex-col gap-2">
                  {groupNotes.map((note) => (
                    <div
                      key={note.id}
                      onClick={() => openNoteDetail(note.id)}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        openNoteInNewWindow(note.id);
                      }}
                      onContextMenu={(e) => handleContextMenu(e, note)}
                      className="cursor-pointer"
                      draggable="true"
                      onDragStart={(e) => handleDragStart(e, note)}
                    >
                      <NoteCard
                        note={note}
                        toggleCompletion={toggleTodoCompletion}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )
          ))}

          {hasMore && (
            <div className="flex items-center justify-center my-4">
              <button
                onClick={fetchMoreNotes}
                disabled={loadingMore}
                className="px-3 py-2 m-4 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-400 disabled:bg-gray-200 disabled:text-gray-500"
              >
                {loadingMore ? t('common.loading') : t('notes.loadMore')}
              </button>
            </div>
          )}
        </div>

        {isDesktop && selectedNote && (
          <div
            className="pointer-events-none fixed right-0 hidden md:block z-50"
            style={{ top: 0, height: '100vh', width: `${panelWidth}px` }}
          >
            {/* Resize Handle - Invisible hit area */}
            <div
              className="pointer-events-auto absolute left-0 top-0 bottom-0 w-2 -ml-1 cursor-ew-resize z-50 group"
              onMouseDown={handleMouseDown}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize panel"
            >
              {/* Visual indicator */}
              <div className={`absolute left-1/2 top-0 bottom-0 w-0.5 -ml-px transition-all ${
                isResizing ? 'bg-orange-500' : 'bg-gray-300 opacity-0 group-hover:opacity-100'
              }`} />
            </div>
            
            <div className="pointer-events-auto h-full w-full">
              <NoteDetail
                note={selectedNote}
                onClose={closeNoteDetail}
                onDelete={handleNoteDelete}
                variant="sidebar"
              />
            </div>
          </div>
        )}
      </div>

      {!isDesktop && selectedNote && (
        <NoteDetail 
          note={selectedNote} 
          onClose={closeNoteDetail} 
          onDelete={handleNoteDelete}
        />
      )}

      {/* Context Menu */}
      <NoteContextMenu
        visible={contextMenu.visible}
        x={contextMenu.x}
        y={contextMenu.y}
        note={contextMenu.note}
        onClose={closeContextMenu}
        onPin={togglePinStatus}
        onMove={handleMoveNote}
        onChangeType={handleChangeNoteType}
        onShare={handleShareNote}
        onPrint={handlePrintNote}
        onResend={handleResendEmail}
        onDelete={handleDeleteNote}
        secondaryEmails={userData?.secondarySendEmails || []}
        primaryEmail={userData?.sendEmailAddress || userData?.email || t('common.primaryEmail')}
      />

      {/* Folder Selection Modal */}
      <FolderSelectionModal
        isOpen={isFolderModalOpen}
        onClose={() => {
          setIsFolderModalOpen(false);
          setNoteToMove(null);
        }}
        onSelectFolder={handleFolderSelect}
        currentFolderId={noteToMove?.folderId}
      />

      {/* Share Modal */}
      <NoteShareModal
        isOpen={isShareModalOpen}
        note={noteToShare}
        onClose={() => {
          setIsShareModalOpen(false);
          setNoteToShare(null);
        }}
      />

      {/* Undo Delete Toast */}
      {showUndoToast && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white px-4 py-3 rounded-lg shadow-lg z-50 flex items-center gap-4">
          <span>
            {t('notes.delete.deleted', { count: pendingDeleteNotes.length })}
          </span>
          <button
            onClick={undoDelete}
            className="px-3 py-1 bg-white text-gray-800 rounded hover:bg-gray-100 transition-colors"
          >
            {t('notes.delete.undo')}
          </button>
        </div>
      )}
    </div>
  );
}

export default Notes;
